import React, { Component } from "react";
import PropTypes from "prop-types";
import { wrapComponent } from "../utils/container";
import UserAvatar from "../components/UserAvatar";
import { Tab, Tabs, Typography, Button } from "material-ui";
import {
    resetUserProfile,
    userCorrections,
    userPosts,
    userProfileInit
} from "../actions/userProfile.actions";
import LoadingError from "../components/LoadingError";
import PostScroll from "../components/PostScroll";
import {
    determineCorrectionAction,
    newCorrection,
    submitComment,
    toggleCorrectionReaction
} from "../actions/correction.actions";
import CorrectionScroll from "../components/CorrectionScroll";
import UserLanguages from "../components/UserLanguages";
import { push } from "react-router-redux";
import CorrectionPostDialogs from "./CorrectionPostDialogs";
import EditProfileDialog from "./EditProfileDialog";
import { openProfile } from "../actions/editProfile.actions";
import { defaultName } from "../utils/userUtils";
import {
    determinePostAction,
    togglePostReaction
} from "../actions/post.actions";

const styleSheet = theme => {
    return {
        mainContainer: {
            margin: "0 auto",
            width: "100%",
            maxWidth: 600
        },
        mainContainerError: {
            margin: "150px auto",
            width: "100%",
            maxWidth: 600
        },
        headerContainer: {},
        header: {
            height: 150,
            borderRadius: "0 0 2px 2px",
            backgroundColor: theme.palette.primary[200]
        },
        avatarCenter: {
            cursor: "default",
            margin: "-30px auto 0 auto",
            border: "2px solid white"
        },
        displayName: {
            margin: `${theme.spacing.unit * 2}px auto`,
            fontWeight: "bold",
            textAlign: "center",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis"
        },
        userLanguages: {
            margin: `${theme.spacing.unit * 2}px auto`
        },
        editProfile: {
            textAlign: "center",
            margin: `${theme.spacing.unit * 3}px auto ${theme.spacing.unit *
                2}px auto`
        },
        editProfileButton: {
            backgroundColor: theme.palette.primary[400]
        }
    };
};

const postTab = 0;
const correctionTab = 1;

function getUID(props) {
    return props.match.params.uid;
}

class UserProfilePage extends Component {
    state = {
        selectedTab: postTab
    };

    static propTypes = {
        userSettings: PropTypes.object.isRequired,
        userRequest: PropTypes.object.isRequired,
        currentUser: PropTypes.object.isRequired,
        correctionsRequest: PropTypes.object.isRequired,
        postsRequest: PropTypes.object.isRequired,
        classes: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
        dispatch: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired
    };

    resetAndInit(uid) {
        this.props.dispatch(resetUserProfile());
        this.props.dispatch(userProfileInit({ uid }));
    }

    componentWillMount() {
        const uid = getUID(this.props);
        this.resetAndInit(uid);
    }

    componentWillReceiveProps(nextProps) {
        const uid = getUID(this.props);
        const nextUID = getUID(nextProps);
        if (nextUID !== uid) {
            this.resetAndInit(nextUID);
        }
    }

    onEditProfile = () => {
        const {
            userSettings: { displayName, languages, emailNotifications },
            dispatch
        } = this.props;
        dispatch(openProfile({ displayName, languages, emailNotifications }));
    };

    isCurrentUser = () =>
        this.props.currentUser.id === this.props.userSettings.id;

    onPostMenuClick = ({ post, action }) => {
        this.props.dispatch(determinePostAction(action)({ post }));
    };

    onCorrectionMenuItemClick = ({ correction, action }) => {
        this.props.dispatch(determineCorrectionAction(action)({ correction }));
    };

    renderUser() {
        const { classes, t } = this.props;
        const { displayName, avatarURL, languages } = this.props.userSettings;
        let editProfile;
        if (this.isCurrentUser()) {
            editProfile = (
                <React.Fragment>
                    <div className={classes.editProfile}>
                        <Button
                            className={classes.editProfileButton}
                            onClick={this.onEditProfile}
                            raised
                            color="primary">
                            {t("userProfile.Edit profile")}
                        </Button>
                    </div>
                    <EditProfileDialog />
                </React.Fragment>
            );
        }
        return (
            <div className={classes.headerContainer}>
                <div className={classes.header} />
                <UserAvatar
                    className={classes.avatarCenter}
                    size="md"
                    displayName={defaultName(displayName)}
                    avatarURL={avatarURL}
                />
                <Typography className={classes.displayName} type="headline">
                    {defaultName(displayName)}
                </Typography>
                <UserLanguages
                    className={classes.userLanguages}
                    languages={languages}
                />
                {editProfile}
            </div>
        );
    }

    loadMorePosts = () => {
        this.props.dispatch(
            userPosts({
                uid: getUID(this.props),
                after: this.props.postsRequest.pageInfo.endCursor
            })
        );
    };

    loadMoreCorrections = () => {
        this.props.dispatch(
            userCorrections({
                uid: getUID(this.props),
                after: this.props.correctionsRequest.pageInfo.endCursor
            })
        );
    };

    renderPosts() {
        const {
            postsRequest: {
                request: { errors },
                edges,
                pageInfo: { hasNextPage }
            },
            t,
            currentUser
        } = this.props;

        if (errors && errors.count() > 0) {
            return (
                <LoadingError
                    message={t(
                        "userProfile.Something went wrong loading the feed"
                    )}
                    onRetry={this.loadMorePosts}
                />
            );
        }
        let emptyStateMessage;
        if (this.isCurrentUser()) {
            emptyStateMessage = "userProfile.You have no posts";
        } else {
            emptyStateMessage = "userProfile.This user has no posts";
        }
        return (
            <PostScroll
                onCorrectionMenuItemClick={this.onCorrectionMenuItemClick}
                onMenuClick={this.onPostMenuClick}
                currentUser={currentUser}
                emptyStateMessage={t(emptyStateMessage)}
                onCorrectionReactionButtonClick={
                    this.onCorrectionReactionButtonClick
                }
                onSubmitComment={this.onSubmitComment}
                onReactionButtonClick={this.onReactionButtonClick}
                onCorrectionButtonClick={this.handleCorrectionButtonClick}
                edges={edges}
                hasMore={hasNextPage}
                loadMore={this.loadMorePosts}
            />
        );
    }

    onSubmitComment = payload => {
        this.props.dispatch(submitComment(payload));
    };

    onCorrectionReactionButtonClick = payload => {
        this.props.dispatch(toggleCorrectionReaction(payload));
    };

    onReactionButtonClick = payload => {
        this.props.dispatch(togglePostReaction(payload));
    };

    renderCorrections() {
        const {
            t,
            correctionsRequest: {
                request: { errors },
                edges,
                pageInfo: { hasNextPage }
            },
            currentUser
        } = this.props;

        if (errors && errors.count() > 0) {
            return (
                <LoadingError
                    message={t(
                        "userProfile.Something went wrong loading the corrections"
                    )}
                    onRetry={this.loadMoreCorrections}
                />
            );
        }

        let emptyStateMessage;
        if (this.isCurrentUser()) {
            emptyStateMessage = "userProfile.You have no corrections";
        } else {
            emptyStateMessage = "userProfile.This user has no corrections";
        }

        return (
            <CorrectionScroll
                onCorrectionMenuItemClick={this.onCorrectionMenuItemClick}
                currentUserId={currentUser.id}
                onReactionButtonClick={this.onCorrectionReactionButtonClick}
                onGotoPost={this.onGotoPost}
                emptyStateMessage={t(emptyStateMessage)}
                edges={edges}
                hasMore={hasNextPage}
                loadMore={this.loadMoreCorrections}
            />
        );
    }

    handleTabChange = (event, value) => {
        this.setState({ selectedTab: value });
    };

    renderTabs() {
        const { classes, t } = this.props;
        const { selectedTab } = this.state;

        if (!this.props.userSettings.id) {
            return;
        }
        return (
            <div className={classes.root}>
                <Tabs value={selectedTab} onChange={this.handleTabChange}>
                    <Tab label={t("userProfile.Posts")} />
                    <Tab label={t("userProfile.Feedback")} />
                </Tabs>
                {selectedTab === postTab && this.renderPosts()}
                {selectedTab === correctionTab && this.renderCorrections()}
            </div>
        );
    }

    handleCorrectionButtonClick = post => {
        return () => {
            this.props.dispatch(newCorrection({ post }));
        };
    };

    gotoFeed = () => {
        this.props.dispatch(push("/feed"));
    };

    onGotoPost = correction => {
        this.props.dispatch(push(`/posts/${correction.postId}`));
    };

    render() {
        const { t, classes, userRequest, userSettings } = this.props;

        if (userRequest.errors.count() > 0) {
            return (
                <div className={classes.mainContainerError}>
                    <LoadingError
                        message={t(
                            "userProfile.We couldn't find the user you were looking for"
                        )}
                        onRetry={this.gotoFeed}
                        retryText={t("userProfile.Go to your feed")}
                    />
                </div>
            );
        } else if (!userSettings.id) {
            return <div />;
        }
        return (
            <div className={classes.mainContainer}>
                {this.renderUser()}
                {this.renderTabs()}
                <CorrectionPostDialogs />
            </div>
        );
    }
}

function mapStateToProps({
    userProfile: {
        userSettings,
        correctionsRequest,
        postsRequest,
        userRequest
    },
    userSettings: currentUser
}) {
    return {
        userSettings,
        correctionsRequest,
        postsRequest,
        userRequest,
        currentUser
    };
}

export default wrapComponent(UserProfilePage, styleSheet, mapStateToProps);



// WEBPACK FOOTER //
// ./src/containers/UserProfilePage.jsx